import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { Mutations } from './mutations';
import { TaskViewMutationTypes } from './mutation-types';
import { TaskViewActionTypes } from './action-types';
import {
  ClaimTaskPayload,
  TaskApiOptions,
  TaskApiResponse,
  UnClaimTaskPayload,
  QuickRejectTaskPayload,
  RejectTaskPayload,
} from './types';

import ServerAPI, {
  combineQueryParams,
  handleError,
} from '@/services/serverAPI';
import { TaskStatus } from '@/enums/Tasks';

type AugmentedAction = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, 'commit'>;

export interface Actions {
  [TaskViewActionTypes.GET_TASKS](
    { commit }: AugmentedAction,
    payload: TaskApiOptions
  ): Promise<void>;
  [TaskViewActionTypes.GET_TASKS_ALL](
    { commit }: AugmentedAction,
    payload: TaskApiOptions
  ): Promise<void>;
  [TaskViewActionTypes.CLAIM_TASK](
    { commit }: AugmentedAction,
    payload: ClaimTaskPayload
  ): Promise<void>;
  [TaskViewActionTypes.UN_CLAIM_TASK](
    { commit }: AugmentedAction,
    payload: UnClaimTaskPayload
  ): Promise<void>;
  [TaskViewActionTypes.REJECT_TASK](
    { commit }: AugmentedAction,
    payload: RejectTaskPayload
  ): Promise<void>;
  [TaskViewActionTypes.QUICK_REJECT_TASK](
    { commit }: AugmentedAction,
    payload: QuickRejectTaskPayload
  ): Promise<any>;
  [TaskViewActionTypes.SET_TASKS_LOADING](
    { commit }: AugmentedAction,
    payload: boolean
  ): Promise<void>;
  [TaskViewActionTypes.GET_NEW_TASKS_COUNT]({
    commit,
  }: AugmentedAction): Promise<void>;
  [TaskViewActionTypes.GET_NEW_TASKS_ALL_COUNT]({
    commit,
  }: AugmentedAction): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [TaskViewActionTypes.GET_TASKS](
    { commit },
    filterOptions: TaskApiOptions
  ) {
    commit(TaskViewMutationTypes.TASK_STORE_SET_LIST_SETTINGS, filterOptions);

    try {
      const paramString = combineQueryParams({
        size: filterOptions.pageSize,
        start: filterOptions.pageStart,
        filters: filterOptions.filters,
        ['sort_descending']: filterOptions.sortDesc,
        ['sort_by']: filterOptions.sortKey,
      });

      let filters = '';

      if (!filterOptions.viewDone) {
        filters += `&filters=task_status:${TaskStatus.TODO},${TaskStatus.NOT_STARTED},${TaskStatus.IN_PROGRESS}`;
      }

      const taskList: TaskApiResponse = await ServerAPI.get(
        `/tasks${paramString}${filters}`
      );

      commit(TaskViewMutationTypes.TASK_STORE_SET_LIST, {
        taskList: taskList.results,
        totalResultCount: taskList.count,
      });
    } catch (e) {
      handleError(e);
      commit(TaskViewMutationTypes.TASK_STORE_SET_LIST, {
        taskList: [],
        totalResultCount: 0,
      });
    }
  },
  async [TaskViewActionTypes.GET_TASKS_ALL](
    { commit },
    filterOptions: TaskApiOptions
  ) {
    commit(TaskViewMutationTypes.TASK_STORE_SET_LIST_ALL_SETTINGS, filterOptions);

    try {
      const paramString = combineQueryParams({
        size: filterOptions.pageSize,
        start: filterOptions.pageStart,
        filters: filterOptions.filters,
        ['sort_descending']: filterOptions.sortDesc,
        ['sort_by']: filterOptions.sortKey,
      });

      let filters = '';

      if (!filterOptions.viewDone) {
        filters += `&filters=task_status:${TaskStatus.TODO},${TaskStatus.NOT_STARTED},${TaskStatus.IN_PROGRESS}`;
      }
      filters += '&filters=show_all:true';

      const taskList: TaskApiResponse = await ServerAPI.get(
        `/tasks${paramString}${filters}`
      );
      commit(TaskViewMutationTypes.TASK_STORE_SET_LIST_ALL, {
        taskList: taskList.results,
        totalResultCount: taskList.count,
      });
    } catch (e) {
      handleError(e);
      commit(TaskViewMutationTypes.TASK_STORE_SET_LIST_ALL, {
        taskList: [],
        totalResultCount: 0,
      });
    }
  },
  async [TaskViewActionTypes.CLAIM_TASK](_, payload) {
    try {
      await ServerAPI.patch(`/tasks/${payload.taskId}/claim`, {});
    } catch (e) {
      handleError(e);
    }
  },
  async [TaskViewActionTypes.UN_CLAIM_TASK](_, payload) {
    try {
      await ServerAPI.patch(`/tasks/${payload.taskId}/unclaim`, {});
    } catch (e) {
      handleError(e);
    }
  },
  async [TaskViewActionTypes.REJECT_TASK](_, payload) {
    try {
      await ServerAPI.patch(`/tasks/${payload.taskId}/reject`, payload.opportunities);
    } catch (e) {
      handleError(e);
    }
  },
  async [TaskViewActionTypes.QUICK_REJECT_TASK](_, payload) {
    try {
      const response = await ServerAPI.get(`/tasks/${payload.taskId}/quick-reject`);
      return response;
    } catch (e) {
      handleError(e);
      return null;
    }
  },
  async [TaskViewActionTypes.SET_TASKS_LOADING]({ commit }, payload) {
    commit(TaskViewMutationTypes.TASK_STORE_SET_LOADING, payload);
  },
  async [TaskViewActionTypes.GET_NEW_TASKS_COUNT]({ commit }) {
    try {
      const paramString = combineQueryParams({
        size: 1,
        start: 0,
      });

      let filters = '';
      filters += `&filters=task_status:${TaskStatus.TODO}`;
      filters += '&filters=show_in_task_list:true';
      filters += '&filters=managing_owners:MANAGED'; // JTI-5553

      const tasksRes: TaskApiResponse = await ServerAPI.get(
        `/tasks${paramString}${filters}`
      );

      commit(TaskViewMutationTypes.SET_NEW_TASKS_COUNT, tasksRes.count);
    } catch (e) {
      commit(TaskViewMutationTypes.SET_NEW_TASKS_COUNT, 0);
    }
  },
  async [TaskViewActionTypes.GET_NEW_TASKS_ALL_COUNT]({ commit }) {
    try {
      const paramString = combineQueryParams({
        size: 1,
        start: 0,
      });
      let filters = '';
      filters += `&filters=task_status:${TaskStatus.TODO}`;
      filters += '&filters=show_all:true';
      filters += '&filters=managing_owners:MANAGED'; // JTI-5553

      const tasksRes: TaskApiResponse = await ServerAPI.get(
        `/tasks${paramString}${filters}`
      );

      commit(TaskViewMutationTypes.SET_NEW_TASKS_ALL_COUNT, tasksRes.count);
    } catch (e) {
      commit(TaskViewMutationTypes.SET_NEW_TASKS_ALL_COUNT, 0);
    }
  },
};
